import classNames from 'classnames';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { IPredicate, ISegmentUsage, IUniquePredicate, Segment } from '@feathr/blackbox';
import { CampaignClass } from '@feathr/blackbox';
import { Alert, Button, CardV2 as Card, Spinner, toast } from '@feathr/components';
import SaveAsNewButton from '@feathr/extender/App/DataSegmentsPage/DataSegmentPage/SaveAsNewButton';
import GroupEditWarning from '@feathr/extender/components/GroupEditWarning';
import { useStore } from '@feathr/extender/state';
import {
  generateKeys,
  getIconForAction,
  logUserEvents,
  objectId,
  removeElementAtIndex,
  useToggle,
} from '@feathr/hooks';
import { RerunModal, useFlags } from '@feathr/report_components';

import Glue from '../Glue';
import SegmentBuilderAI from '../SegmentBuilderAI';
import GroupModeControls from './GroupModeControls';
import Predicates from './Predicates/Predicates';
import SegmentGauge from './SegmentGauge';

import styles from './EditFilters.css';

// Stable reference so the isEphemeral resync effect doesn't re-fire on every render
// when a segment has no predicates set yet (e.g. an in-flight Segments.get() placeholder).
const EMPTY_PREDICATES = observable.array<IPredicate>([]);

interface IProps {
  filterContext?:
    | 'campaign'
    | 'flight'
    | 'segment'
    | 'campaignGoal'
    | 'flightGoal'
    | 'facebookCampaign';
  className?: string;
  hideAlert?: boolean;
  hideGauge?: boolean;
  mode?: 'count' | 'crumbs' | 'reachable' | 'emails';
  segment: Segment;
  disabled?: boolean;
}

function EditFilters({
  className,
  disabled = false,
  filterContext = 'segment',
  hideAlert = false,
  hideGauge = false,
  mode = 'count',
  segment,
}: IProps): JSX.Element {
  const { Conversions } = useStore();
  const { campaignId } = useParams<{ campaignId?: string }>();
  const { t } = useTranslation();
  const [usage, setUsage] = useState<ISegmentUsage>();
  const [isRerunModalOpen, toggleRerunModalOpen] = useToggle(false);
  const [isAIModalOpen, toggleAIModalOpen] = useToggle(false);
  const { aiGroupBuilder } = useFlags();

  // Predicates do not have a unique identifier, so we need to add one.
  const [localPredicates, setLocalPredicates] = useState(
    observable.array(generateKeys(segment.get('predicates') || [])),
  );

  const { canClone, canEdit, isReadOnly } = segment.permissions;
  const isDisabled = isReadOnly || canEdit === false || disabled;

  const showNoPermissionsAlert = !hideAlert && !segment.get('parent_segment') && canEdit === false;
  const predicates = segment.get('predicates') ?? EMPTY_PREDICATES;

  const getUsage = useCallback(async () => {
    const segmentUsage = await segment.getUsage();
    setUsage(segmentUsage);
  }, [segment]);

  useEffect(() => {
    getUsage();
  }, [getUsage]);

  useEffect(() => {
    runInAction(() => {
      setLocalPredicates(observable.array(generateKeys(segment.get('predicates') || [])));
    });
  }, [segment, segment.isFetched]);

  useEffect(() => {
    if (segment.isEphemeral) {
      runInAction(() => {
        setLocalPredicates(observable.array(generateKeys(predicates || [])));
      });
    }
  }, [predicates]);

  if (!usage) {
    return <Spinner />;
  }

  const affectedGoals = usage.goals.length;
  const hasFacebook = usage.active_campaigns.some((cpn) => cpn._cls === CampaignClass.Facebook);

  const handleAddFilterV2 = action((): void => {
    const newPredicate: IUniquePredicate = {
      /*
       * Use key on predicate to give them a unique identifier
       * and prevent re-renders when indices change.
       */
      key: objectId(),
      kind: 'activity',
      attr_against: 'loc_url',
      attr_type: 'string',
      comparison: 'eq',
      value: '',
      unit: 'days',
      group: [],
    };

    const newPredicates = [...localPredicates, newPredicate];
    setLocalPredicates(observable.array(newPredicates));
    /*
     * We don't want to patch the predicates with keys as it will cause a re render
     * since they are not returned with keys.
     */
    runInAction(() => {
      segment.set({
        predicates: newPredicates,
      });
    });
  });

  const handleRemoveFilter = action((index: number): void => {
    const newPredicates = removeElementAtIndex(localPredicates, index);
    setLocalPredicates(observable.array(newPredicates));
    /*
     * We don't want to patch the predicates with keys as it will cause a re render
     * since they are not returned with keys.
     */
    runInAction(() => {
      segment.set({ predicates: newPredicates });
    });
  });

  const handleSegmentGenerated = action((newPredicates: IPredicate[]): void => {
    // Add unique keys to predicates for rendering
    const predicatesWithKeys = generateKeys(newPredicates);
    setLocalPredicates(observable.array(predicatesWithKeys));
    segment.set({ predicates: predicatesWithKeys });
  });

  async function handleConfirmRerun(): Promise<void> {
    const params = new URLSearchParams({
      segment: segment.id,
    });
    try {
      await Conversions.rerun(params);
      runInAction(() => {
        segment.set({ rerun: true });
      });
    } catch (e) {
      toast(t("Couldn't recalculate conversions. Try again."), {
        type: 'error',
      });
      throw e;
    }
  }

  const permissionsAlertDescription = canClone && (
    <>
      <SaveAsNewButton group={segment} prefix={''} type={'link'}>
        {t('Save as new')}
      </SaveAsNewButton>{' '}
      {t('in order to add additional filters.')}
    </>
  );

  const noPermissionsAlert = showNoPermissionsAlert && (
    <Alert
      description={permissionsAlertDescription}
      title={t("You don't have permissions to edit this group.")}
      type={'info'}
    />
  );

  const AddFilterButton = (
    <Button
      disabled={isDisabled}
      name={'add_filter'}
      onClick={handleAddFilterV2}
      prefix={getIconForAction('add')}
    >
      {t('Add filter set')}
    </Button>
  );

  function handleOpenAIModal(): void {
    logUserEvents({ clicked_pixl_group_builder: null });
    toggleAIModalOpen();
  }

  const AIFilterButton = aiGroupBuilder && (
    <Button
      disabled={isDisabled}
      name={'ai_filter'}
      onClick={handleOpenAIModal}
      prefix={getIconForAction('magic')}
      suffix={<span className={styles.betaPill}>Beta</span>}
      tooltip={t('Groups generated by Pixl Group Builder use AI and may not be accurate')}
      tooltipPosition={'top-end'}
      type={'recommendationSecondary'}
    >
      {t('Pixl Group builder')}
    </Button>
  );

  return (
    <div className={className}>
      {noPermissionsAlert}
      {!!affectedGoals && !isDisabled && (
        <GroupEditWarning affectedGoals={affectedGoals} onRecalculate={toggleRerunModalOpen} />
      )}

      {isRerunModalOpen && (
        <RerunModal
          context={'Group'}
          id={segment.id}
          name={segment.name}
          onClose={toggleRerunModalOpen}
          onConfirm={handleConfirmRerun}
        />
      )}

      {hasFacebook && (
        <Alert
          title={t(
            'Targets being used by active Meta campaigns currently only support the "URL" filter.',
          )}
          type={'warning'}
        />
      )}

      <Card theme={isDisabled ? 'disabled' : 'default'} width={'full'}>
        <Card.Content padding={'compact'}>
          <GroupModeControls disabled={isDisabled} group={segment} />
        </Card.Content>
        {localPredicates.length > 0 && (
          <Card.Content
            contentClassName={classNames(styles.content, className)}
            padding={'compact'}
          >
            {localPredicates.map((localPredicate, index) => {
              const lastItemIndex = localPredicates.length - 1;

              function handleRemove(): void {
                handleRemoveFilter(index);
              }

              return (
                <Fragment key={localPredicate.key}>
                  <Predicates
                    disabled={isDisabled}
                    excludeIds={campaignId ? [campaignId] : undefined}
                    model={segment}
                    onClickRemove={handleRemove}
                    optionsGroup={filterContext}
                    predicate={localPredicate}
                    setPredicates={setLocalPredicates}
                  />
                  <Glue
                    className={styles.glue}
                    isLastItem={index === lastItemIndex}
                    mode={segment.get('mode')}
                    t={t}
                  />
                </Fragment>
              );
            })}
          </Card.Content>
        )}

        {!isDisabled && (
          <Card.Actions>
            <div className={styles.buttonGroup}>
              {AddFilterButton}
              {AIFilterButton}
            </div>
          </Card.Actions>
        )}
      </Card>

      {!hideGauge && <SegmentGauge mode={mode} segment={segment} />}

      {isAIModalOpen && (
        <SegmentBuilderAI
          isOpen={isAIModalOpen}
          onClose={toggleAIModalOpen}
          onSegmentGenerated={handleSegmentGenerated}
          segment={segment}
        />
      )}
    </div>
  );
}

export default observer(EditFilters);
